// Shared TypeScript interfaces for the frontend

// Device Profile IDs - ThingsBoard device profile identifiers
export const DEVICE_PROFILE_IDS = {
  MODBERRY: '24d6b7a0-b039-11ee-b45b-ebfecced3ed2',
  NH3_SENSOR: 'f224fd90-5334-11ef-99b6-b76bb08af6e8',
  BARN_THERMOMETER: '0c8dbef0-b430-11ee-8546-138d993829eb',
  CELLAR_LASER: '252cc6e0-b039-11ee-b45b-ebfecced3ed2',
} as const;

export const FLOW_STALENESS_THRESHOLD_MS = 30 * 60 * 60 * 1000;

// Device interfaces
export interface Device {
  deviceId: string;
  name: string;
  farmName: string;
  farmAssetId?: string;
  province?: string;
  barnName?: string;
  isActive: boolean;
  deviceProfileId?: string;
  deviceProfileName?: string;
  telemetryHealthScore?: number;
  lastTelemetryCheck?: string;
  lastActivityTime?: string | Date;
  updatedAt?: string | Date;
  discoveryMethod?: string;
  sprinklingSchedulerEnabled?: boolean;
  modelType?: string;
  modelVersion?: string;
  commissionedAt?: string;
  offlineSince?: string | Date;
  offlineDurationMs?: number;
  liquidFlowRate?: number;
  waterFlowRate?: number;
  mixtureSprayFlowRate?: number;
  liquidToMixtankFlowRate?: number;
  primingSprayFlowRate?: number;
  dosingEfficiency?: number;
  isExperimental?: boolean;
  isProduction?: boolean;
  mgClTankEmptyEstimate?: string;
  mixerCurrentVolume?: number;
  barnSprinklingCutoffTemperature?: number; // Celsius - minimum temp for dosing
  activeAlarms?: Array<{ id: string; type: string; severity: string }>;
  // Optional firmware information (camelCase from API)
  buildInfo?: string | {
    branch?: string;
    gitHash?: string;
  };
  // Flow rate metrics from backend
  metrics?: {
    liquidFlowRate?: number;
    waterFlowRate?: number;
    dosingFlowRate?: number;
    mixtureSprayFlowRate?: number;
    liquidToMixtankFlowRate?: number;
    dosingEfficiency?: number;
    status: 'healthy' | 'warning' | 'critical' | 'offline';
  };
}

// Farm asset interface (from ThingsBoard assets API)
export interface FarmAsset {
  id: { entityType: string; id: string };
  name: string;
  label: string;
  type: string;
  assetProfileId: { entityType: string; id: string };
  coordinates?: { latitude: number; longitude: number };
  province?: string;
}

// Memory usage interface
export interface MemoryUsage {
  rss: number;
  heapTotal: number;
  heapUsed: number;
  external: number;
  arrayBuffers: number;
}

// Remote.it device interface
export interface RemoteItDevice {
  id: string;
  name: string;
  state: string;
  online: boolean;
  lastReported?: string;
  statusA?: string;
  statusB?: string;
  statusC?: string;
  statusD?: string;
  statusE?: string;
  platform?: number;
  tags?: RemoteItTag[];
  owner?: {
    id: string;
    email: string;
  };
  services?: RemoteItService[];
}

export interface RemoteItTag {
  name: string;
  value?: string;
}

export interface RemoteItService {
  id: string;
  name: string;
  type: string;
  port: number;
  state: string;
  enabled: boolean;
}

// Firmware monitoring interfaces
export interface FirmwareInfo {
  deviceId: string;
  name: string;
  farmName: string;
  isActive: boolean;
  isExperimental: boolean;
  isProduction: boolean;
  // Primary firmware info (from ThingsBoard buildInfo)
  branch?: string;
  gitHash?: string;
  // Remote.it supplementary data (if available)
  remoteitBranch?: string;  // statusA - for comparison/verification
  remoteitCommit?: string;  // statusB - for comparison/verification  
  diskUsage?: number;       // statusC - disk space monitoring
  // Analysis
  commitAge?: number;
  healthStatus: 'healthy' | 'warning' | 'critical' | 'unknown';
}

// Enhanced device with GitLab commit information
export interface DeviceWithCommitInfo extends FirmwareInfo {
  buildInfo?: {
    branch?: string;
    gitHash?: string;
  };
  commitInfo?: {
    commitDate?: string;
    ageInDays?: number;
  };
}

// Flow measurement interface
export interface FlowMeasurement {
  id: string;
  deviceId: string;
  measurementType: 'liquid_dosing' | 'mixtank_dosing';
  timestampMs: number;
  flowRateLpm?: number;
  volumeTarget?: number;
  volumeActual?: number;
  dosingDurationSec?: number;
  mixtankVolume?: number;
  sprinklerRun?: string;
  wasCancelled: boolean;
  rawData?: {
    sprinkleType?: string;
    currentMixTankVolume?: number;
    volumeThatHasBeenDosed?: number;
    volumeToDose?: number;
    targetFluid?: string;
    averageFlowRate?: number;
    dosingAverageFlowinLPM?: number;
    secondsInDosingElapsed?: number;
    targetSprinklerRun?: string;
    [key: string]: unknown;
  };
}

// API response interfaces
export interface DeviceAlert {
  type: string;
  message: string;
  severity: string;
  timestamp: string;
}

export interface DeviceAnomaly {
  type: string;
  description: string;
  severity: string;
  confidence: number | string;
  timestamp: string;
  suggestion?: string;
}

export interface Anomaly {
  id: string;
  deviceId: string;
  farmName: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  detectedAt: string;
  anomalyType?: string;
  confidenceScore?: number;
  acknowledged?: boolean;
  suggestion?: string;
}

export interface DailyOpsSummaryItem {
  deviceId: string;
  farmName: string;
  category: 'offline' | 'blockage' | 'degradation' | 'calibration';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  suggestion?: string;
  since?: string;
  durationHours?: number;
}

export interface DailyOpsSummary {
  status: 'healthy' | 'attention_needed';
  generatedAt: string;
  fleet: {
    totalCommissioned: number;
    activeLastDay: number;
    uptimePercent: number;
  };
  attentionItems: DailyOpsSummaryItem[];
  summary: string;
}

// Performance metrics from flow analysis (shared across device-detail components)
export interface PerformanceMetrics {
  avgWaterToMixtankLpm?: number;
  avgLiquidToMixtankLpm?: number;
  avgPrimingSprayLpm?: number;
  avgMixtureSprayLpm?: number;
  /** Timestamp (ms) of last dosing event — used for age display and staleness checks */
  lastDosingTimestamp?: number;
}

// Device status response interface
export interface DeviceStatus {
  deviceId: string;
  deviceName: string;
  farmName: string;
  barnName?: string;
  province?: string;
  location?: { latitude: number; longitude: number };
  status: string;
  liquidFlowRate: number;
  dosingEfficiency: number;
  healthScore: number;
  lastUpdate: string;
  alerts: DeviceAlert[];
  anomalies: DeviceAnomaly[];
  _raw?: {
    /** The underlying device record from the monitoring service */
    device?: {
      sprinklingSchedulerEnabled?: boolean;
      commissionedAt?: string;
      sectionGroupsEnabled?: boolean;
      sectionGroups?: Record<string, string>;
      lastActivityTime?: string;
      buildInfo?: { branch?: string; gitHash?: string; buildDate?: string };
      status?: string;
      liquidToMixtankFlowRate?: number;
      dosingFlowRate?: number;
    };
    performanceMetrics?: PerformanceMetrics;
    latestFlowMeasurement?: FlowMeasurement;
    latestMixtankMeasurement?: FlowMeasurement;
  };
}

// Cellar depth response from /api/devices/:deviceId/cellar-depth
export interface CellarDepthResponse {
  available: boolean;
  reason?: 'no_barn' | 'no_cellar_laser';
  cellarDeviceId?: string;
  cellarDeviceName?: string;
  hasData?: boolean;
  depthCm?: number | null;
  fullnessPercent?: number | null;
  volumeM3?: number | null;
  timestamp?: number;
}

// System status interfaces
export interface SystemStatus {
  timestamp: string;
  mcp: {
    url: string;
    connected: boolean;
    error: string | null;
  };
  thingsboard: {
    connected: boolean;
    url: string;
    environment: 'production' | 'development' | 'unknown';
    error: string | null;
  };
  services: {
    backend: {
      version: string;
      uptime: number;
      memory: MemoryUsage;
    };
    database: {
      connected: boolean;
      error: string | null;
    };
    redis: {
      connected: boolean;
      status: string;
    };
    ai: {
      enabled: boolean;
      provider?: string;
      status?: string;
      error?: string;
    };
    n8n?: {
      enabled: boolean;
      connected: boolean;
      status?: string;
      error?: string;
    };
    tbMcp?: {
      connected: boolean;
      error: string | null;
    };
    remoteit?: {
      enabled: boolean;
      connected: boolean;
      status?: string;
      error?: string | null;
    };
    komodo?: {
      enabled: boolean;
      connected: boolean;
      error?: string | null;
    };
    mongodb?: {
      connected: boolean;
      error?: string | null;
    };
  };
  remoteIt?: {
    enabled: boolean;
    status?: string;
    error?: string;
  };
}

// Remote.it API interfaces
export interface RemoteItUser {
  id: string;
  email: string;
  devices: RemoteItDevice[];
}

export interface RemoteItStatus {
  enabled: boolean;
  consecutiveFailures: number;
  lastFailureTime: number;
  mcpUrl: string;
}

// Component prop interfaces
export interface DeviceCardMetrics {
  liquidFlowRate?: number;
  dosingEfficiency?: number;
  status: 'healthy' | 'warning' | 'critical' | 'offline';
}

// System version interfaces
export interface ComponentVersion {
  name: string;
  version: string;
  description?: string;
  buildDate?: string;
  buildTime?: string;
  gitCommit?: string;
  gitBranch?: string;
  nodeVersion?: string;
  fullVersion?: string;
  status: 'running' | 'connected' | 'external' | 'error' | 'unavailable';
  error?: string;
  note?: string;
  dependencies?: Record<string, string>;
  // Frontend-specific fields
  nextVersion?: string;
  reactVersion?: string;
  framework?: string;
  runtime?: string;
}

export interface SystemVersions {
  timestamp: string;
  components: {
    backend: ComponentVersion;
    tbmcp: ComponentVersion;
    database: ComponentVersion;
    redis: ComponentVersion;
    frontend: ComponentVersion;
    remoteit_mcp: ComponentVersion;
    n8n?: ComponentVersion;
    komodo?: ComponentVersion;
    mongodb?: ComponentVersion;
  };
}

// Farm Deployment Types (mirrors backend FarmDeploymentResponse)

/** A device within a barn deployment, enriched with type-specific latest readings */
export interface DeploymentDevice {
  deviceId: string;
  name: string;
  deviceProfileId: string | null;
  isActive: boolean;
  lastActivityTime: string | null;
  // Modberry-specific
  status?: DeviceStatus;
  // NH3 sensor-specific
  latestNh3Ppm?: number | null;
  nh3Timestamp?: string | null;
  // Barn thermometer-specific
  latestTemperature?: number | null;
  temperatureTimestamp?: string | null;
  // Cellar laser-specific
  cellarDepth?: CellarDepthResponse | null;
}

/** A barn within a farm deployment */
export interface BarnDeployment {
  assetId: string;
  name: string;
  label: string | null;
  devices: DeploymentDevice[];
}

/** Complete farm deployment view response */
export interface FarmDeploymentResponse {
  farm: {
    assetId: string;
    name: string;
    label: string | null;
    latitude: number | null;
    longitude: number | null;
    province: string | null;
  };
  barns: BarnDeployment[];
}

// Device Discovery Review Types
export interface PendingReviewDevice {
  device_id: string;
  device_name: string;
  device_data: {
    deviceId: string;
    name: string;
    label?: string;
    deviceProfileId?: string;
    deviceProfileName?: string;
    lastActivityTime?: number;
  };
  classification_data: {
    isProduction: boolean;
    confidence: number;
    reasons: string[];
    recommendedAction: 'auto_register' | 'manual_review' | 'ignore';
  };
  created_at: string;
}